import { useImperativeHandle, useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';

import { TextField, type TextFieldProps } from '../../components/TextField';
import { cn } from '../../lib/utils';

export interface RHFTextFieldProps {
    name: string;
    type?: TextFieldProps['type'];
    label: TextFieldProps['label'];
    defaultValue?: TextFieldProps['defaultValue'];
    placeholder?: TextFieldProps['placeholder'];
    description?: TextFieldProps['description'];
    dataTestid?: TextFieldProps['dataTestid'];
    labelHtmlAttributes?: TextFieldProps['labelHtmlAttributes'];
    inputHtmlAttributes?: TextFieldProps['inputHtmlAttributes'];
    className?: TextFieldProps['className'];
    inputClassName?: TextFieldProps['inputClassName'];
    required?: TextFieldProps['required'];
    disabled?: TextFieldProps['disabled'];
    onAfterChange?: TextFieldProps['onChange'];
    prefix?: TextFieldProps['prefix'];
    suffix?: TextFieldProps['suffix'];
    icon?: IconDefinition;
    size?: TextFieldProps['size'];
}

export const RHFTextField = ({
    name,
    type,
    label,
    defaultValue,
    placeholder,
    description,
    dataTestid,
    labelHtmlAttributes,
    inputHtmlAttributes,
    className,
    inputClassName,
    required,
    disabled,
    onAfterChange,
    prefix,
    suffix,
    icon,
    size,
}: RHFTextFieldProps) => {
    const { control } = useFormContext();
    const { field, fieldState } = useController({
        control,
        name,
        defaultValue,
    });

    // Inside your RHFTextField component
    const inputRef = useRef<HTMLInputElement | null>(null);

    useImperativeHandle(field.ref, () => inputRef.current);

    return (
        <div className={cn('flex w-full gap-3', className)}>
            {icon && (
                <FontAwesomeIcon
                    className={clsx('relative size-[24px] text-gray-500', {
                        'bottom-[-32px]': label,
                        'bottom-[-16px]': !label,
                    })}
                    icon={icon}
                />
            )}
            <TextField
                id={field.name}
                name={field.name}
                type={type}
                label={label}
                value={field.value ?? ''}
                defaultValue={field.value}
                placeholder={placeholder}
                description={description}
                dataTestid={dataTestid}
                labelHtmlAttributes={labelHtmlAttributes}
                inputHtmlAttributes={inputHtmlAttributes}
                className='w-full'
                inputClassName={inputClassName}
                required={required}
                disabled={disabled}
                prefix={prefix}
                suffix={suffix}
                size={size}
                onChange={(...args) => {
                    field.onChange(...args);
                    if (typeof onAfterChange === 'function') {
                        onAfterChange(...args);
                    }
                }}
                onBlur={field.onBlur}
                inputRef={inputRef}
                errorMessage={fieldState.error?.message}
            />
        </div>
    );
};
